import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Volume2, VolumeX, Play, Upload, Check, Music } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const SOUND_PRESETS = [
  { name: 'Classic Chime', url: '/sounds/classic-chime.wav' },
  { name: 'Soft Bell', url: '/sounds/soft-bell.wav' },
  { name: 'Success Tone', url: '/sounds/success-tone.wav' },
  { name: 'Power Up', url: '/sounds/power-up.wav' },
];

export const resolveSoundUrl = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

export const LoginSoundSettings = () => {
  const [soundUrl, setSoundUrl] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [soundFile, setSoundFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings/login-sound`)
      .then((r) => {
        setSoundUrl(r.data.sound_url);
        setEnabled(r.data.enabled !== false);
      })
      .catch(() => {});
  }, []);

  const playPreview = (url) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(resolveSoundUrl(url));
    audio.volume = 0.7;
    audioRef.current = audio;
    audio.play().catch(() => toast.error('Could not play sound'));
  };

  const saveSound = async (url, newEnabled) => {
    setIsSaving(true);
    try {
      const r = await axios.put(`${API}/settings/login-sound`, {
        sound_url: url,
        enabled: newEnabled,
      });
      setSoundUrl(r.data.sound_url);
      setEnabled(r.data.enabled !== false);
      toast.success('Login sound updated');
    } catch (e) {
      toast.error('Failed to update login sound');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!soundFile) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', soundFile);
      const r = await axios.post(`${API}/settings/login-sound/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSoundUrl(r.data.sound_url);
      setEnabled(true);
      setSoundFile(null);
      toast.success('Custom login sound uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload sound');
    } finally {
      setIsUploading(false);
    }
  };

  const isCustom = soundUrl && !SOUND_PRESETS.some((p) => p.url === soundUrl);

  return (
    <div className="glass-card p-6" data-testid="login-sound-settings">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-500/20 rounded-lg">
            {enabled ? (
              <Volume2 className="w-6 h-6 text-green-400" />
            ) : (
              <VolumeX className="w-6 h-6 text-slate-400" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">App Launch Sound</h2>
            <p className="text-sm text-slate-400">Sound played when users log in</p>
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => saveSound(soundUrl, checked)}
          disabled={isSaving}
          data-testid="login-sound-enabled-switch"
        />
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-slate-900 font-semibold">Preset Sounds</Label>
          <div className="mt-2 space-y-2">
            {SOUND_PRESETS.map((preset) => {
              const isActive = soundUrl === preset.url;
              return (
                <div
                  key={preset.url}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                    isActive ? 'border-green-500 bg-green-500/10' : 'border-gray-200 bg-gray-50'
                  }`}
                  data-testid={`sound-preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-2">
                    <Music className="w-4 h-4 text-slate-400" />
                    <span className="text-gray-900 text-sm font-medium">{preset.name}</span>
                    {isActive && <Check className="w-4 h-4 text-green-400" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => playPreview(preset.url)}
                      className="h-8 px-3"
                      data-testid={`preview-sound-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Play className="w-3.5 h-3.5" />
                    </Button>
                    {!isActive && (
                      <Button
                        size="sm"
                        onClick={() => saveSound(preset.url, true)}
                        disabled={isSaving}
                        className="h-8 px-3 bg-green-600 hover:bg-green-700"
                        data-testid={`use-sound-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        Use
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {isCustom && (
          <div className="flex items-center justify-between p-3 rounded-lg border-2 border-green-500 bg-green-500/10">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-green-400" />
              <span className="text-white text-sm font-medium">Custom Sound</span>
              <Check className="w-4 h-4 text-green-400" />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => playPreview(soundUrl)}
              className="h-8 px-3"
              data-testid="preview-custom-sound"
            >
              <Play className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        <div>
          <Label htmlFor="sound-file" className="text-slate-900 font-semibold">Upload Custom Sound</Label>
          <Input
            id="sound-file"
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
            onChange={(e) => setSoundFile(e.target.files[0])}
            className="mt-1 bg-white border-2 border-slate-300 text-slate-900 file:text-slate-900 focus:border-blue-500"
            data-testid="sound-file-input"
          />
          {soundFile && <p className="text-sm text-slate-400 mt-2">Selected: {soundFile.name}</p>}
          <p className="text-xs text-slate-400 mt-1">MP3, WAV, OGG, M4A or AAC — max 5MB</p>
        </div>

        <Button
          onClick={handleUpload}
          disabled={isUploading || !soundFile}
          className="w-full bg-gradient-to-r from-green-500 to-green-600"
          data-testid="upload-sound-btn"
        >
          {isUploading ? (
            <>
              <Upload className="w-4 h-4 mr-2 upload-icon-uploading" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2 upload-icon-idle" />
              Upload Sound
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LoginSoundSettings;
